/**
 * Shared API Client with automatic JWT token injection
 *
 * Usage:
 *   import { apiClient } from "../utils/apiClient";
 *
 *   // GET request
 *   const response = await apiClient.get("/customer?CustomerID=1");
 *
 *   // POST request
 *   const response = await apiClient.post("/customer", { CompanyName: "Test" });
 *
 *   // PUT request
 *   const response = await apiClient.put("/customer", { CustomerID: 1, CompanyName: "Updated" });
 *
 *   // DELETE request
 *   const response = await apiClient.delete("/customer?CustomerID=1");
 */

import axios from "axios";
import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import { resolveAuditEvent } from "./auditMap";
import {
  availableOrganizations,
  getOrganization,
  setPermittedOrganizations,
} from "./organizationScope";
import { markRecordRefused } from "./recordAccess";

// Create axios instance with base URL
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor that adds the JWT to the Authorization header and the
 * selected organization to X-Organization.
 *
 * The organization rides here, beside the token, so that no call site can
 * forget it: every list, search, count and write the SPA makes is scoped by
 * this one header (docs/changes/organization-scoping/SPEC.md section 3c). The
 * public share-link portal uses plain axios and deliberately sends neither.
 */
const addAuthToken = async (config) => {
  // The page this request belongs to, so a refusal of its record is shown on
  // that page and not on whichever one is open when the answer arrives.
  config.__page = window.location.pathname;
  const organization = getOrganization();
  if (organization) {
    config.headers = { ...config.headers, "X-Organization": organization };
  }

  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  } catch {
    // User may not be authenticated - let the request proceed
    // API Gateway will return 401 if authentication is required
  }

  return config;
};

/**
 * Audit trail: after a successful mutation on a known endpoint, tell the
 * audit Lambda to snapshot the affected row (see src/utils/auditMap.js and
 * lambda/mysql-audit.mjs). Failures are swallowed — an audit outage must
 * never block or fail the user's actual save.
 */
const AUDIT_WAIT_MS = 1500;

const sendAuditEvent = (auditEvent) =>
  apiClient.post("/audit", auditEvent, { __skipAudit: true }).catch(() => {
    // Swallow: auditing is best-effort and must not surface to users.
  });

/** Wait for the audit call, but never longer than AUDIT_WAIT_MS. */
const sendAuditEventBounded = (auditEvent) =>
  Promise.race([
    sendAuditEvent(auditEvent),
    new Promise((resolve) => setTimeout(resolve, AUDIT_WAIT_MS)),
  ]);

/**
 * The audit call must complete BEFORE the save response is handed back to
 * the form: several forms alert() + reload/navigate immediately after a
 * successful save, and a fire-and-forget request gets killed by the page
 * unload before it is dispatched (the alert also freezes the token fetch).
 * Bounded await — a slow/down audit service delays a save by at most 1.5s.
 */
const recordMutationAudit = async (response) => {
  // DELETEs are audited pre-flight (below) so the snapshot precedes the row's removal.
  if ((response.config?.method || "").toLowerCase() === "delete")
    return response;
  try {
    const auditEvent = resolveAuditEvent(response.config, response.data);
    if (auditEvent) await sendAuditEventBounded(auditEvent);
  } catch {
    // Never let audit bookkeeping break a successful response.
  }
  return response;
};

/**
 * For DELETEs, the snapshot must be captured BEFORE the row disappears, so
 * we wait (briefly, capped) for the audit call before letting the delete go
 * out. Errors and timeouts are swallowed — the delete always proceeds.
 */
const auditBeforeDelete = async (config) => {
  if ((config.method || "").toLowerCase() !== "delete") return config;
  try {
    const auditEvent = resolveAuditEvent(config, null);
    if (auditEvent) await sendAuditEventBounded(auditEvent);
  } catch {
    // Never block the delete on audit problems.
  }
  return config;
};

/** The code the router puts in a 403's body for an organization the user was not given. */
export const ORGANIZATION_NOT_ALLOWED = "organizationNotAllowed";

let loadingPermitted = null;

/**
 * Asks the router which organizations this user may use and hands the answer
 * to organizationScope, which moves the selection if it has to
 * (organization-scoping 3.6b). One request at a time: callers arriving while
 * it is in flight share it.
 *
 * A failure keeps what the scope already had - every configured organization
 * the first time. The screen offering one it should not is not a leak: the
 * router refuses it with a 403, which brings the list back here.
 */
export function loadPermittedOrganizations() {
  if (!loadingPermitted) {
    loadingPermitted = apiClient
      .get("/my-organizations")
      .then(({ data }) => {
        if (Array.isArray(data?.organizations))
          setPermittedOrganizations(data.organizations);
      })
      .catch(() => {})
      .finally(() => {
        loadingPermitted = null;
      });
  }
  return loadingPermitted;
}

/**
 * Response interceptor for error handling
 */
const handleResponseError = async (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;

    if (status === 401) {
      // Authentication error - token may be expired
      // Optionally redirect to login or refresh token
    } else if (status === 403 && data?.error === ORGANIZATION_NOT_ALLOWED) {
      // An Admin took this organization away. Reload the list, which moves the
      // selection; App.jsx re-keys the routes on that, so the screen re-fetches
      // under one the user may use. The refused request is not retried - it
      // still rejects, and its screen shows the error. A request sent before
      // an earlier reload moved the selection reloads nothing: the list is
      // already the one that refused it.
      const refusedUnder = error.config?.headers?.["X-Organization"];
      if (!refusedUnder || refusedUnder === getOrganization())
        await loadPermittedOrganizations();
      // Still one this user may use after the reload: the header was not what
      // the router refused, the record was - a job or company of the other
      // organization, reached by a link (3.6c). Its page says so.
      if (
        refusedUnder &&
        availableOrganizations().some(({ id }) => id === refusedUnder)
      )
        markRecordRefused(error.config?.__page);
    }

    // Report server errors (5xx) to Sentry
    if (status >= 500) {
      Sentry.captureException(error, {
        extra: { status, url: error.config?.url, method: error.config?.method },
      });
    }

    // Return the error data if available
    return Promise.reject(data || error);
  }

  // Network error or request cancelled — report to Sentry
  Sentry.captureException(error);
  return Promise.reject(error);
};

apiClient.interceptors.request.use(addAuthToken, (error) =>
  Promise.reject(error),
);
apiClient.interceptors.request.use(auditBeforeDelete, (error) =>
  Promise.reject(error),
);
apiClient.interceptors.response.use(recordMutationAudit, handleResponseError);

/**
 * Helper function to build API URL with query parameters
 *
 * Usage:
 *   const url = buildUrl("/customer", { CustomerID: 1, status: "active" });
 *   // Returns: "/customer?CustomerID=1&status=active"
 */
export const buildUrl = (path, params = {}) => {
  const queryString = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");

  return queryString ? `${path}?${queryString}` : path;
};

export default apiClient;
